/**
 * Backend endpoint configuration — the app's two URLs, resolved once at startup.
 *
 * Precedence, highest first:
 *   localStorage override  →  build-time REACT_APP_* env var  →  hardcoded default
 *
 * The override applies in DEVELOPMENT ONLY — see BACKEND_OVERRIDE_ALLOWED. A
 * production build resolves from the env var and the default alone.
 *
 * The override is what the "Backend server" box in Settings writes. It is read
 * once at module load (Apollo captures the URLs when its links are built), so
 * changing it requires a page reload — setBackendOverride's callers do that.
 *
 * The defaults are ONLY a development convenience. A production build should
 * always be made with the REACT_APP_* vars set (see the Amplify env config);
 * assertConfigInProduction turns forgetting that into a loud console error
 * instead of a silently wrong backend.
 *  GRAPHQL_HTTP_URL=https://stagingapi.bolodhan.com/graphql
GRAPHQL_WS_URL=wss://stagingapi.bolodhan.com/graphql

 */
/**
 * The last resort, used only when no REACT_APP_API_URL was set at build time.
 *
 * STAGING, NOT A DEV TUNNEL. This file is committed, so whatever is written
 * here is what a deployed build falls back to when its environment is missing
 * or has not reached the build step — and it had drifted to whichever ngrok
 * tunnel a developer was using that week. A deploy that lost its env var then
 * called a laptop, which is offline to everyone else, and the real server saw
 * no traffic at all while the app looked merely broken.
 *
 * Change the backend for local work in `.env`, which is gitignored and beats
 * this. Never point this at a tunnel.
 */
const FALLBACK_BACKEND_URL = 'https://stagingapi.bolodhan.com/graphql';

/**
 * THE DEFAULTS ARE THE ENV VAR when there is one.
 *
 * So `DEFAULT_BACKEND_URL` and `REACT_APP_API_URL` are the same string by
 * construction rather than by somebody remembering to edit both.
 */
const DEFAULT_BACKEND_URL = process.env.REACT_APP_API_URL || FALLBACK_BACKEND_URL;

/**
 * The socket follows the HTTP endpoint unless it is named outright.
 *
 * Derived rather than declared, because a hand-written pair can name two
 * different hosts — queries going to one backend and subscriptions to another
 * is a fault that shows up as "live updates stopped working" and nothing else.
 */
const DEFAULT_WS_URL = process.env.REACT_APP_GRAPHQL_WS_URL
  || DEFAULT_BACKEND_URL.replace(/^http/i, 'ws');

/** localStorage key holding the operator's backend override (an HTTP URL). */
export const BACKEND_OVERRIDE_KEY = 'backendUrlOverride';
/** Optional overrides for the two endpoints otherwise derived from it. */
export const BACKEND_WS_OVERRIDE_KEY = 'backendWsUrlOverride';
export const BACKEND_GOALS_OVERRIDE_KEY = 'backendGoalsUrlOverride';
const ALL_OVERRIDE_KEYS = [BACKEND_OVERRIDE_KEY, BACKEND_WS_OVERRIDE_KEY, BACKEND_GOALS_OVERRIDE_KEY];

/**
 * A PRODUCTION BUILD NEVER TAKES ITS BACKEND FROM THE BROWSER.
 *
 * The override is a development tool: switch a local session between servers
 * without a rebuild. In a deployed build it was a trap. A URL saved once — by a
 * developer testing against an ngrok tunnel on the live site — outranked the
 * REACT_APP_API_URL the deploy was built with, lived in that browser forever,
 * survived every redeploy, and said nothing on screen. When the tunnel died,
 * login failed with a CORS error from a host the deployed code never mentions.
 * It happened twice (exospherical, then racoon).
 *
 * It is also a phishing route: login posts the password to whatever server the
 * override names, and the box that set it was on every seat's Profile page.
 *
 * So in production the override is neither read nor written, the controls that
 * set it are not shown, and a value left behind by an older build is deleted on
 * load — the browsers already stuck recover on their next visit, with nothing
 * for the user to clear. `npm start` (development) keeps all of it.
 */
export const BACKEND_OVERRIDE_ALLOWED = process.env.NODE_ENV !== 'production';

if (!BACKEND_OVERRIDE_ALLOWED) {
  try {
    ALL_OVERRIDE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage unavailable — there is nothing stored to be stuck on either */
  }
}

let hasWarnedAboutDefaults = false;

const assertConfigInProduction = () => {
  if (process.env.NODE_ENV !== 'production' || hasWarnedAboutDefaults) {
    return;
  }

  const missing = [];
  if (!process.env.REACT_APP_API_URL) missing.push('REACT_APP_API_URL');
  if (!process.env.REACT_APP_GRAPHQL_WS_URL) missing.push('REACT_APP_GRAPHQL_WS_URL');

  if (missing.length > 0) {
    hasWarnedAboutDefaults = true;
    console.error(
      `[config] Production build is missing backend env vars: ${missing.join(', ')}. ` +
      `Falling back to the built-in default (${FALLBACK_BACKEND_URL}). ` +
      `Set these REACT_APP_* variables in your deploy environment and rebuild.`
    );
  }
};

/**
 * Turn whatever an operator typed into the pair of endpoints the app needs.
 *
 * Accepts a bare host (`localhost:8005`), a scheme-less host, a ws:// URL or a
 * full `https://host/graphql`; appends `/graphql` when it is missing and derives
 * the WebSocket URL from the HTTP one. Returns null when the input cannot be a
 * URL, so callers can reject it instead of writing a broken override.
 */
export const normalizeBackendUrl = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;

  // A scheme-less host is assumed to be TLS, except on the loopback/private
  // addresses where a dev backend is almost always plain http.
  const isLocal = /^(localhost|127\.|0\.0\.0\.0|\[?::1\]?|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i.test(trimmed);
  const withScheme = /^[a-z]+:\/\//i.test(trimmed)
    ? trimmed
    : `${isLocal ? 'http' : 'https'}://${trimmed}`;

  let url;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (!url.hostname) return null;

  // A ws:// URL is a reasonable thing to paste; the WS endpoint is derived, so
  // fold it back to its HTTP equivalent first.
  if (url.protocol === 'ws:') url.protocol = 'http:';
  if (url.protocol === 'wss:') url.protocol = 'https:';
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  // Everything the app talks to hangs off the one GraphQL endpoint.
  const path = url.pathname.replace(/\/+$/, '');
  url.pathname = path.endsWith('/graphql') ? path : `${path}/graphql`;
  url.search = '';
  url.hash = '';

  const API_URL = url.toString();
  return {
    API_URL,
    GRAPHQL_WS_URL: API_URL.replace(/^http/, 'ws'),
  };
};

/** The saved override, or '' when none is set (or storage is unavailable). */
export const getBackendOverride = () => {
  if (!BACKEND_OVERRIDE_ALLOWED) return '';
  try {
    return localStorage.getItem(BACKEND_OVERRIDE_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Persist an override. Returns the normalised endpoints, or null if the input
 * was not a usable URL (in which case nothing is written).
 */
export const setBackendOverride = (raw) => {
  if (!BACKEND_OVERRIDE_ALLOWED) return null;
  const next = normalizeBackendUrl(raw);
  if (!next) return null;
  try {
    localStorage.setItem(BACKEND_OVERRIDE_KEY, next.API_URL);
  } catch {
    return null;
  }
  return next;
};

/** Drop every override and fall back to env vars / defaults. */
export const clearBackendOverride = () => {
  try {
    ALL_OVERRIDE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage unavailable — nothing to clear */
  }
};

/**
 * A subscriptions URL as typed: ws:// or wss:// kept, http(s):// turned into
 * its socket form, a bare host given wss (ws on a local address), and
 * `/graphql` appended when missing. Null when it cannot be a URL.
 */
export const normalizeWsUrl = (raw) => {
  const http = normalizeBackendUrl(String(raw || '').trim().replace(/^ws(s?):\/\//i, 'http$1://'));
  return http ? http.API_URL.replace(/^http/, 'ws') : null;
};

/**
 * The goals schema URL as typed: a bare host or a URL without a path gets
 * `/goals/graphql`; a path is kept as given. Null when it cannot be a URL.
 */
export const normalizeGoalsUrl = (raw) => {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  const isLocal = /^(localhost|127\.|0\.0\.0\.0|10\.|192\.168\.)/i.test(trimmed);
  let url;
  try {
    url = new URL(/^[a-z]+:\/\//i.test(trimmed) ? trimmed : `${isLocal ? 'http' : 'https'}://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  const path = url.pathname.replace(/\/+$/, '');
  url.pathname = path ? path : '/goals/graphql';
  url.search = '';
  url.hash = '';
  return url.toString();
};

const readKey = (key) => {
  if (!BACKEND_OVERRIDE_ALLOWED) return '';
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

/** The three saved overrides, '' for any not set. */
export const getBackendOverrides = () => ({
  api: readKey(BACKEND_OVERRIDE_KEY),
  ws: readKey(BACKEND_WS_OVERRIDE_KEY),
  goals: readKey(BACKEND_GOALS_OVERRIDE_KEY),
});

/**
 * Save the three endpoints together. A blank one removes its override (so a
 * blank subscriptions or goals URL goes back to being derived from the GraphQL
 * one). Nothing is written unless every given URL is usable; returns
 * `{ ok, errors }` with an error per unusable field.
 */
export const setBackendOverrides = ({ api = '', ws = '', goals = '' }) => {
  if (!BACKEND_OVERRIDE_ALLOWED) return { ok: false, errors: { api: 'Not available in a production build' } };
  const next = {
    [BACKEND_OVERRIDE_KEY]: api.trim() ? normalizeBackendUrl(api)?.API_URL ?? null : '',
    [BACKEND_WS_OVERRIDE_KEY]: ws.trim() ? normalizeWsUrl(ws) : '',
    [BACKEND_GOALS_OVERRIDE_KEY]: goals.trim() ? normalizeGoalsUrl(goals) : '',
  };
  const errors = {};
  if (next[BACKEND_OVERRIDE_KEY] === null) errors.api = 'Not a usable URL';
  if (next[BACKEND_WS_OVERRIDE_KEY] === null) errors.ws = 'Not a usable URL';
  if (next[BACKEND_GOALS_OVERRIDE_KEY] === null) errors.goals = 'Not a usable URL';
  if (Object.keys(errors).length) return { ok: false, errors };
  try {
    Object.entries(next).forEach(([k, v]) => (v ? localStorage.setItem(k, v) : localStorage.removeItem(k)));
  } catch {
    return { ok: false, errors: { api: 'Browser storage is unavailable' } };
  }
  return { ok: true, errors: {} };
};

/**
 * What the app would use with no override in play.
 *
 * The `|| process.env...` that used to be repeated here is gone: the defaults
 * ARE the env vars now (see their declarations), so naming them twice only
 * created a second place for the two to disagree. `source` still distinguishes
 * a configured build from one running on the built-in fallback, which is what
 * the Settings screen reports.
 */
/* The goals schema, mounted beside the main one: `/graphql` → `/goals/graphql`. */
const goalsFrom = (apiUrl) => apiUrl.replace(/\/graphql\/?$/, '/goals/graphql');

export const getDefaultConfig = () => ({
  API_URL: DEFAULT_BACKEND_URL,
  GRAPHQL_WS_URL: DEFAULT_WS_URL,
  GOALS_API_URL: goalsFrom(DEFAULT_BACKEND_URL),
  source: process.env.REACT_APP_API_URL ? 'env' : 'default',
});

/**
 * Resolve the active configuration. Safe to call repeatedly; warns once.
 *
 * Each endpoint takes its own override when one is saved. Otherwise the
 * subscriptions and goals URLs follow the GraphQL one — the overridden one
 * when it is overridden, so moving the backend moves all three together.
 */
export const getCurrentConfig = () => {
  assertConfigInProduction();

  const defaults = getDefaultConfig();
  const saved = getBackendOverrides();
  const api = normalizeBackendUrl(saved.api);
  const ws = normalizeWsUrl(saved.ws);
  const goals = normalizeGoalsUrl(saved.goals);
  if (!api && !ws && !goals) return defaults;

  const API_URL = api?.API_URL ?? defaults.API_URL;
  return {
    API_URL,
    GRAPHQL_WS_URL: ws ?? (api ? api.GRAPHQL_WS_URL : defaults.GRAPHQL_WS_URL),
    GOALS_API_URL: goals ?? goalsFrom(API_URL),
    source: 'override',
    overridden: { api: Boolean(api), ws: Boolean(ws), goals: Boolean(goals) },
  };
};

const resolved = getCurrentConfig();

/** HTTP GraphQL endpoint. */
export const API_URL = resolved.API_URL;

/** WebSocket GraphQL endpoint (subscriptions). */
export const GRAPHQL_WS_URL = resolved.GRAPHQL_WS_URL;

/**
 * The goals schema, mounted beside the main one on the same host.
 *
 * A handful of staff operations — the catalog and asset-model cache warms —
 * live on `/goals/graphql` rather than `/graphql`, so they are sent with this
 * as the operation's `uri`. Derived from the GraphQL URL unless it has its
 * own override.
 */
export const GOALS_API_URL = resolved.GOALS_API_URL;

/** Where the active endpoints came from: 'override' | 'env' | 'default'. */
export const CONFIG_SOURCE = resolved.source;
